import React, { useContext, useRef, useState } from 'react'
import { Wrapper, SectionWrapper, TabsWrapper, InstructionsWrapper, CouponDetailWrapper } from './styled'
import { AppContext } from 'contexts/AppProvider'
import { displayPrice } from 'helpers'
import { getMembershipCardBgByTier, getMembershipNameByTier, MEMBERSHIP_TIER } from 'helpers/membership'
import {
  convertListFPMemberCouponsToCoupons,
  convertFpMemberCampaignsToCouponDetails,
  getThumbnailFpCampaignById,
} from 'helpers/coupon'
import { useTranslation } from 'react-i18next'
import * as Sentry from '@sentry/browser'
import { useListFpMemberCouponsQuery } from 'graphQL/dinein/generate/operations'
import { useListPointRedemptionCampaignsQuery, useRedeemPointsMutation } from 'graphQL/hatostore/generate/operations'
import { dineInClient, liffApiLimitClient } from 'variables/services'
import { isDevelopment } from 'variables/environment'
import Tabs, { TabSetting } from 'components/base-ui/Tabs/Tabs'
import UserCard from 'components/dine-in/UserCard/UserCard'
import { Coupon, CouponDetailType } from 'types/coupon'
import CouponList from 'components/dine-in/CouponList'
import InstructionsCombineCoupon from 'components/dine-in/InstructionsCombineCoupon'
import OverlayPage from 'components/dine-in/OverlayPage'
import useModal from 'hooks/useModal'
import CouponDetail from 'components/dine-in/CouponDetail'
import RedeemCampaign from 'components/dine-in/RedeemCampaign'

enum TabKey {
  COUPON = 'COUPON',
  REDEEM = 'REDEEM',
}

interface ShareCouponProps {
  orderId: number
  onClickCombineCoupon: VoidFunction
  onRefresh: VoidFunction
  processing: boolean
  allowedSharingCoupon: boolean
  toggleAlertLabel: (message: string) => void
}

const ShareCoupon = ({
  orderId,
  onRefresh,
  onClickCombineCoupon,
  processing,
  allowedSharingCoupon,
  toggleAlertLabel,
}: ShareCouponProps) => {
  const { currentMembership, currentUser } = useContext(AppContext)
  const { t } = useTranslation()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [selectedCoupon, setSelectedCoupon] = useState<CouponDetailType>()
  const [campaigns, setCampaigns] = useState<CouponDetailType[]>([])
  const [activeTab, setActiveTab] = useState(TabKey.COUPON)
  const couponDetail = useModal()
  const readyToRefresh = useRef(true)
  const modalInvalid = useModal()
  const modalRedeemPoints = useModal()

  const {
    loading: loadingListFpMemberCoupons,
    refetch: refetchListFpMemberCoupons,
    data: listFpMemberCouponsData,
  } = useListFpMemberCouponsQuery({
    client: dineInClient,
    variables: { orderID: orderId },
    skip: orderId === 0,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ listFPMemberCoupons }) => {
      const fpCoupons = convertListFPMemberCouponsToCoupons({
        listFPMemberCoupons,
        currentMembershipId: currentMembership?.id,
        allowedSharingCoupon,
      })
      setCoupons(fpCoupons)
    },
  })

  const {
    loading: loadingFpCampaigns,
    refetch: refetchListPointRedemtionCampaigns,
    data: fpCampaignsData,
  } = useListPointRedemptionCampaignsQuery({
    client: liffApiLimitClient,
    fetchPolicy: 'no-cache',
    variables: { limit: 9999, offset: 0 },
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ listPointRedemptionCampaigns }) => {
      const fpCampaigns = convertFpMemberCampaignsToCouponDetails(listPointRedemptionCampaigns)
      setCampaigns(fpCampaigns)
    },
  })

  const handleRefreshData = async () => {
    if (loadingListFpMemberCoupons || loadingFpCampaigns || processing || !readyToRefresh.current) return
    readyToRefresh.current = false
    refetchListFpMemberCoupons()
    refetchListPointRedemtionCampaigns()
    onRefresh()
    setTimeout(() => (readyToRefresh.current = true), 2000)
  }

  const [redeemPoints] = useRedeemPointsMutation({
    client: liffApiLimitClient,
    onCompleted: ({ redeemPoints }) => {
      const { success, invalidConditions, failureReason } = redeemPoints
      couponDetail.setProcessing(false)
      modalRedeemPoints.setOpen(false)

      if (!success || invalidConditions?.length > 0 || failureReason) {
        modalInvalid.setOpen(true)

        const invalidData = {
          invalidConditions: invalidConditions?.toString(),
          failureReason,
        }
        !isDevelopment && Sentry.captureException(new Error(`Cannot redeem points: ${JSON.stringify(invalidData)}`))
        return
      }

      handleRefreshData()
      setActiveTab(TabKey.COUPON)
      couponDetail.setOpen(false)
      setSelectedCoupon(undefined)
      toggleAlertLabel(t('dineIn.shareCouponPage.redeemPointsSuccess'))
    },
    onError: (error) => {
      couponDetail.setProcessing(false)
      modalRedeemPoints.setOpen(false)
      modalInvalid.setOpen(true)
      !isDevelopment && Sentry.captureException(new Error(`Error from redeem points: ${JSON.stringify(error)}`))
    },
  })

  if (!currentMembership) return null

  const handleClickRedemption = () => {
    if (couponDetail.processing || !selectedCoupon) {
      return
    }

    couponDetail.setProcessing(true)
    redeemPoints({
      variables: {
        pointRedemptionCampaignID: Number(selectedCoupon.serialNumber),
        quantity: 1,
      },
    })
  }

  const handleClickCouponCondition = (serialNumber: string) => {
    const couponSelected = listFpMemberCouponsData?.listFPMemberCoupons.find((c) => c.serialNumber === serialNumber)
    if (!couponSelected) return

    const { imageURL, name, startTime, endTime, description } = couponSelected
    setSelectedCoupon({
      serialNumber,
      thumbnailImageURL: imageURL,
      name,
      startTime,
      endTime,
      pointsRequire: 0,
      description,
      invalidConditions: [],
    })
    couponDetail.setOpen(true)
  }

  const handleClickCampaign = (campaign: CouponDetailType) => {
    if (!fpCampaignsData) return
    const thumbnailImageURL = getThumbnailFpCampaignById(
      Number(campaign.serialNumber),
      fpCampaignsData?.listPointRedemptionCampaigns
    )
    setSelectedCoupon({ ...campaign, thumbnailImageURL })
    couponDetail.setOpen(true)
  }

  const tabs: TabSetting[] = [
    {
      key: TabKey.COUPON,
      title: t('dineIn.shareCouponPage.tabs.coupon'),
      children: (
        <SectionWrapper style={{ paddingBottom: 100 }}>
          <CouponList
            coupons={coupons}
            loading={loadingListFpMemberCoupons}
            onRefresh={handleRefreshData}
            onClickCouponCondition={handleClickCouponCondition}
            onClickRedeem={() => setActiveTab(TabKey.REDEEM)}
            allowedSharingCoupon={allowedSharingCoupon}
          />
          {(allowedSharingCoupon || !loadingListFpMemberCoupons) && (
            <InstructionsWrapper>
              <InstructionsCombineCoupon onClickCombineCoupon={onClickCombineCoupon} />
            </InstructionsWrapper>
          )}
        </SectionWrapper>
      ),
    },
    {
      key: TabKey.REDEEM,
      title: t('dineIn.shareCouponPage.tabs.redeemPoint'),
      children: (
        <SectionWrapper style={{ paddingTop: 12 }}>
          <RedeemCampaign campaigns={campaigns} loading={loadingFpCampaigns} onClickCampaign={handleClickCampaign} />
        </SectionWrapper>
      ),
    },
  ]

  const { firstName, lastName, memberTier, totalPoints } = currentMembership
  const fullName = `${firstName} ${lastName}`
  const tierName = getMembershipNameByTier(memberTier as MEMBERSHIP_TIER)
  const totalPointsWithComma = displayPrice(totalPoints)

  return (
    <Wrapper>
      <SectionWrapper>
        <UserCard
          name={fullName}
          tier={tierName}
          tierImage={getMembershipCardBgByTier(memberTier as MEMBERSHIP_TIER)}
          points={totalPointsWithComma}
          avatarUrl={currentUser.linePicture}
        />
      </SectionWrapper>
      <TabsWrapper>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onSelectTab={(key) => {
            setActiveTab(key as TabKey)
          }}
        />
      </TabsWrapper>
      <OverlayPage isShow={couponDetail.open} animation>
        {selectedCoupon && (
          <CouponDetailWrapper>
            <CouponDetail
              coupon={selectedCoupon}
              loading={couponDetail.processing}
              onClickBack={() => couponDetail.setOpen(false)}
              onRedemption={handleClickRedemption}
              isRedeemable={activeTab === TabKey.REDEEM}
              memberTotalPoints={currentMembership.totalPoints}
              openModalRedeemPoints={modalRedeemPoints.open}
              onCloseModalRedeemPoints={() => modalRedeemPoints.setOpen(false)}
              onClickRedeemPoints={() => modalRedeemPoints.setOpen(true)}
              openModalInvalid={modalInvalid.open}
              onCloseModalInvalid={() => modalInvalid.setOpen(false)}
            />
          </CouponDetailWrapper>
        )}
      </OverlayPage>
    </Wrapper>
  )
}

export default ShareCoupon
